import { KioSelect } from "@/framework/KioForm/common/KioSelect";
import { allowed_languages } from "@/hooks/useAiFunctions";
import { plausibleTrackAiButtonClicked } from "@/hooks/usePlausibleTracking";
import { useStore } from "@/Store";
import { AutoFixHigh, Sync } from "@mui/icons-material";
import { Alert, Button, styled, Tooltip } from "@mui/material";
import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const AiFunctionalityWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 8px;
`;
const ButtonWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: flex-end;
  justify-content: flex-end;
  width: 100%;
  > * {
    min-height: 40px;
  }
`;
const KioSelectWrapper = styled("div")`
  width: 100%;
  max-width: 120px;
`;

const StyledSync = styled(Sync)`
  margin-right: 8px;
  animation: spin 5s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(360deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }
`;
const StyledAutoFixHigh = styled(AutoFixHigh)`
  margin-right: 8px;
`;

const BetaContainer = styled("span")`
  font-size: 0.75rem;
  font-weight: bold;
  margin-left: 8px;
  padding: 0 8px;
  border: 1.5px solid white;
  border-radius: 25px;
`;

const AiGenerateButton: FC<{
  selectedLocale: string;
  label: string;
  tooltip?: string;
  disabled?: boolean;
  loading?: boolean;
  onGenerate: () => void;
  onToggleNynorsk?: (isNynorsk: boolean) => void;
  aiFunctionType?: string;
}> = ({ selectedLocale, label, tooltip, disabled, loading, onGenerate, onToggleNynorsk, aiFunctionType }) => {
  const { t } = useTranslation("common");
  const { state } = useStore();
  const { ownerSlug } = useParams<{ ownerSlug: string }>();
  const [showWarning, setShowWarning] = useState(false);
  const [selectedNorwegianVariant, setSelectedNorwegianVariant] = useState<{ value: string; label: string }>({
    value: "no",
    label: "Bokmål",
  });

  const handleNorwegianLanguageChange = (locale: string | null) => {
    setSelectedNorwegianVariant(() => {
      locale === "nn" ? onToggleNynorsk?.(true) : onToggleNynorsk?.(false);
      return locale === "nn" ? { value: "nn", label: "Nynorsk" } : { value: "no", label: "Bokmål" };
    });
  };

  return (
    <AiFunctionalityWrapper>
      {showWarning && (
        <Alert
          sx={{
            margin: "10px 0 !important",
            padding: "8px 8px !important",
          }}
          severity="warning"
        >
          {t("aiFunctions.generatedByAiWarning")}
        </Alert>
      )}
      {!allowed_languages.includes(selectedLocale) && <p>{t("aiFunctions.languageNotSupported")}</p>}
      <ButtonWrapper>
        {selectedLocale === "no" && (
          <KioSelectWrapper>
            <KioSelect
              onChange={handleNorwegianLanguageChange}
              options={[
                { value: "no", label: "Bokmål" },
                { value: "nn", label: "Nynorsk" },
              ]}
              value={selectedNorwegianVariant.value}
              isHeaderVariant
            />
          </KioSelectWrapper>
        )}
        <Tooltip title={tooltip + " (beta)"}>
          <Button
            style={{ textTransform: "none" }}
            id="schema-menu-button"
            variant="contained"
            color="primary"
            disabled={disabled || !allowed_languages.includes(selectedLocale)}
            onClick={() => {
              plausibleTrackAiButtonClicked(
                aiFunctionType || "unknown",
                ownerSlug || "unknown",
                state?.cmsContextInstance?.owner_id || "unknown"
              );
              setShowWarning(true);
              onGenerate();
            }}
            aria-label={label + " (beta)"}
          >
            {loading ? <StyledSync /> : <StyledAutoFixHigh />} {label} <BetaContainer>BETA</BetaContainer>
          </Button>
        </Tooltip>
      </ButtonWrapper>
    </AiFunctionalityWrapper>
  );
};

export default AiGenerateButton;
